/**
 * UCM报文生成弹窗组件
 * 提供表单输入和报文生成展示功能
 */

import React, { useState, useEffect } from 'react';
import {
    Modal,
    Form,
    Input,
    Select,
    DatePicker,
    Button,
    Space,
    message,
    Alert,
    Typography,
    Divider,
    Spin,
    Row,
    Col,
} from 'antd';
import { CopyOutlined, CheckCircleOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import AirportSelector from '../../../../components/common/AirportSelector';
import { UCM_TYPE_OPTIONS } from '../constants/ucmConstants';
import { ucmService } from '../../../../api/enhancedApi';
import { flightOperationFlightService } from '../../../../api/enhancedApi';

const { TextArea } = Input;
const { Text, Paragraph } = Typography;

const UcmGenerateModal = ({ visible, onCancel, onSuccess }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [ucmContent, setUcmContent] = useState('');
    const [generatedInfo, setGeneratedInfo] = useState(null);
    
    // 航班列表
    const [flightList, setFlightList] = useState([]);
    const [loadingFlights, setLoadingFlights] = useState(false);
    
    // 选中的机场和航班
    const [selectedAirport, setSelectedAirport] = useState(null);
    const [selectedFlightId, setSelectedFlightId] = useState(null);

    // 重置表单和状态
    const resetForm = () => {
        form.resetFields();
        setUcmContent('');
        setGeneratedInfo(null);
        setFlightList([]);
        setSelectedAirport(null);
        setSelectedFlightId(null);
    };

    // 当机场改变时，加载航班列表
    const handleAirportChange = async (airportCode) => {
        // airportCode 现在是 IATA 代码（字符串），如 "PVG", "SHA"
        setSelectedAirport(airportCode);
        setFlightList([]);
        setSelectedFlightId(null); // 清空已选航班
        form.setFieldsValue({
            flight_number: undefined,
            flight_date: undefined,
            aircraft_reg: undefined,
        });

        if (!airportCode) return;

        setLoadingFlights(true);
        try {
            // 计算日期范围：过去7天到未来30天
            const today = dayjs();
            const startDate = today.subtract(7, 'day').format('YYYY-MM-DD');
            const endDate = today.add(30, 'day').format('YYYY-MM-DD');

            const response = await flightOperationFlightService.getFlightOperations({
                departure_airport: airportCode, // 传递IATA代码
                start_date: startDate, // 开始日期：7天前
                end_date: endDate,     // 结束日期：30天后
                status: 'SCHEDULED,DELAYED,DEPARTED,COMPLETED,DIVERTED', // 排除：已取消、计划取消、被替换
                sort_by: 'flight_date', // 按航班日期排序
                sort_order: 'desc',     // 降序：最新的在前
                page: 1,
                page_size: 100, // 获取最近100个航班
            });

            if (response.data?.items) {
                setFlightList(response.data.items);
            }
        } catch (error) {
            console.error('加载航班列表失败:', error);
            message.error('加载航班列表失败');
        } finally {
            setLoadingFlights(false);
        }
    };

    // 当航班改变时，自动填充相关信息
    const handleFlightChange = (flightId) => {
        // flightId 现在是航班ID（数字），确保唯一性
        setSelectedFlightId(flightId);
        const flight = flightList.find(f => f.id === flightId);
        if (flight) {
            form.setFieldsValue({
                flight_number: flight.flight_number, // 更新航班号字段
                flight_date: flight.flight_date ? dayjs(flight.flight_date) : undefined,
                aircraft_reg: flight.aircraft_reg || '',
            });
        }
    };

    // 生成UCM报文
    const handleGenerate = async () => {
        try {
            const values = await form.validateFields();
            
            // 清洗ULD标识符（按行分割，去除空行）
            const uldIdentifiers = values.uld_identifiers
                .split('\n')
                .map(line => line.trim())
                .filter(line => line.length > 0);

            if (uldIdentifiers.length === 0) {
                message.error('请至少输入一个ULD标识符');
                return;
            }

            setGenerating(true);

            const requestData = {
                flight_number: values.flight_number,
                flight_date: values.flight_date.format('YYYY-MM-DD'),
                departure_airport: values.departure_airport,
                aircraft_reg: values.aircraft_reg,
                ucm_type: values.ucm_type,
                uld_identifiers: uldIdentifiers,
            };

            const response = await ucmService.generateUcm(requestData);

            if (response.data?.success && response.data?.data) {
                setUcmContent(response.data.data.ucm_content);
                setGeneratedInfo({
                    history_id: response.data.data.history_id,
                    uld_count: response.data.data.uld_count,
                });
                message.success(response.data.message || 'UCM报文生成成功');
            }
        } catch (error) {
            console.error('生成UCM报文失败:', error);
            message.error(error.response?.data?.detail || '生成UCM报文失败');
        } finally {
            setGenerating(false);
        }
    };

    // 复制报文到剪贴板
    const handleCopy = () => {
        navigator.clipboard.writeText(ucmContent).then(() => {
            message.success('报文已复制到剪贴板');
        }).catch(() => {
            message.error('复制失败，请手动复制');
        });
    };

    // 完成并关闭
    const handleFinish = () => {
        resetForm();
        onSuccess();
        onCancel();
    };

    // 关闭弹窗
    const handleCancel = () => {
        if (ucmContent) {
            Modal.confirm({
                title: '确认关闭',
                content: '已生成的报文将丢失，是否确认关闭？',
                onOk: () => {
                    resetForm();
                    onCancel();
                },
            });
        } else {
            resetForm();
            onCancel();
        }
    };

    return (
        <Modal
            title="生成 UCM 报文"
            open={visible}
            onCancel={handleCancel}
            width={1200}
            footer={null}
            destroyOnClose
        >
            <Spin spinning={loading}>
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{
                        ucm_type: 'OUT',
                    }}
                >
                    {/* 第一行：航班信息和基础字段 */}
                    <Row gutter={16}>
                        {/* 左侧列：航班信息 */}
                        <Col span={12}>
                            <Form.Item
                                label="起飞机场"
                                name="departure_airport"
                                rules={[{ required: true, message: '请选择起飞机场' }]}
                            >
                                <AirportSelector
                                    valueType="iata_code"
                                    placeholder="选择起飞机场"
                                    onChange={handleAirportChange}
                                />
                            </Form.Item>

                            {flightList.length > 0 ? (
                                <Form.Item label="快速选择航班">
                                    <Select
                                        value={selectedFlightId}
                                        placeholder="选择航班号和日期"
                                        loading={loadingFlights}
                                        showSearch
                                        allowClear
                                        optionFilterProp="children"
                                        onChange={handleFlightChange}
                                        onClear={() => {
                                            setSelectedFlightId(null);
                                            form.setFieldsValue({
                                                flight_number: undefined,
                                                flight_date: undefined,
                                                aircraft_reg: undefined,
                                            });
                                        }}
                                    >
                                        {flightList.map(flight => (
                                            <Select.Option key={flight.id} value={flight.id}>
                                                {flight.flight_number} ({dayjs(flight.flight_date).format('YYYY-MM-DD')})
                                                {flight.aircraft_reg && ` - ${flight.aircraft_reg}`}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            ) : null}

                            <Form.Item
                                label="航班号"
                                name="flight_number"
                                rules={[{ required: true, message: '请输入或选择航班号' }]}
                            >
                                <Input placeholder="输入或从上方选择航班自动填充" />
                            </Form.Item>

                            <Form.Item
                                label="航班日期"
                                name="flight_date"
                                rules={[{ required: true, message: '请选择航班日期' }]}
                            >
                                <DatePicker
                                    style={{ width: '100%' }}
                                    format="YYYY-MM-DD"
                                    placeholder="选择航班日期"
                                />
                            </Form.Item>
                        </Col>

                        {/* 右侧列：其他基础信息 */}
                        <Col span={12}>
                            <Form.Item
                                label="飞机注册号"
                                name="aircraft_reg"
                                rules={[{ required: true, message: '请输入飞机注册号' }]}
                            >
                                <Input placeholder="输入飞机注册号" />
                            </Form.Item>

                            <Form.Item
                                label="报文类型"
                                name="ucm_type"
                                rules={[{ required: true, message: '请选择报文类型' }]}
                            >
                                <Select
                                    options={UCM_TYPE_OPTIONS}
                                    placeholder="选择报文类型"
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    {/* 第二行：ULD板号列表独占一整行 */}
                    <Form.Item
                        label="ULD板号列表"
                        name="uld_identifiers"
                        rules={[{ required: true, message: '请输入ULD板号' }]}
                        tooltip="每行一个ULD板号，可直接粘贴多行"
                    >
                        <TextArea
                            rows={8}
                            placeholder="每行输入一个ULD板号，例如：&#10;PMC90003FL&#10;PMC90036FL&#10;PMC90041FL&#10;PMC90053FL&#10;PMC90091FL"
                        />
                    </Form.Item>

                    <Form.Item>
                        <Space>
                            <Button
                                type="primary"
                                onClick={handleGenerate}
                                loading={generating}
                            >
                                生成报文
                            </Button>
                            <Button onClick={handleCancel}>
                                取消
                            </Button>
                        </Space>
                    </Form.Item>
                </Form>

                {ucmContent && (
                    <>
                        <Divider>生成的UCM报文</Divider>
                        
                        {generatedInfo && (
                            <Alert
                                message={`报文生成成功！包含 ${generatedInfo.uld_count} 个ULD`}
                                type="success"
                                icon={<CheckCircleOutlined />}
                                showIcon
                                style={{ marginBottom: 16 }}
                            />
                        )}

                        <div style={{ 
                            background: '#f5f5f5', 
                            padding: '16px', 
                            borderRadius: '4px',
                            marginBottom: '16px',
                        }}>
                            <Paragraph
                                code
                                style={{
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-all',
                                    marginBottom: 0,
                                    fontFamily: 'Courier New, monospace',
                                }}
                            >
                                {ucmContent}
                            </Paragraph>
                        </div>

                        <Space>
                            <Button
                                type="primary"
                                icon={<CopyOutlined />}
                                onClick={handleCopy}
                            >
                                复制报文
                            </Button>
                            <Button
                                type="default"
                                onClick={handleFinish}
                            >
                                完成
                            </Button>
                        </Space>
                    </>
                )}
            </Spin>
        </Modal>
    );
};

export default UcmGenerateModal;

